import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Lock, Unlock, ChevronRight, Clock, Shield, Trash2 } from 'lucide-react';
import KoffieBackButton from './KoffieBackButton';
import { useKoffiePinLock } from './useKoffiePinLock';

const t = {
  bg: '#f2f0eb', white: '#ffffff', text: '#0c0c0b',
  muted: '#8a8780', lines: '#dcd9d1', accent: '#c75a0a',
  sans: "'Jost', system-ui, sans-serif",
  serif: "'Playfair Display', Georgia, serif",
};

// ─── Animations ───────────────────────────────────────────────────────────────

const slideDown = keyframes`
  from { opacity: 0; transform: translateX(-50%) translateY(-8px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
`;

// ─── Styled Components ────────────────────────────────────────────────────────

const Toast = styled.div`
  position: fixed; top: 16px; left: 50%;
  transform: translateX(-50%); z-index: 10000;
  padding: 9px 18px; border-radius: 6px;
  font-family: ${t.sans}; font-size: 13px; font-weight: 500;
  color: #fff; white-space: nowrap;
  animation: ${slideDown} 0.2s ease;
  background: ${p => p.$type === 'error' ? '#ef4444' : t.text};
`;

const Container = styled.div`
  height: 100%; width: 100%;
  background: ${t.bg}; overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const Content = styled.div`
  padding: 36px 40px 80px;
  max-width: 480px;
  margin: 0 auto;
  @media (max-width: 600px) { padding: 24px 20px 64px; }
`;

const PageTitle = styled.h1`
  font-family: ${t.serif};
  font-size: clamp(20px, 3vw, 26px); font-weight: 900;
  color: ${t.text}; letter-spacing: -0.02em; margin: 0 0 4px;
`;

const Hero = styled.div`
  padding: 0 0 24px;
  display: flex; flex-direction: column; align-items: flex-start; gap: 8px;
`;

const HeroBadge = styled.div`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 10px; border-radius: 4px;
  font-family: ${t.sans}; font-size: 11px; font-weight: 500;
  background: ${p => p.$active ? 'rgba(34,197,94,0.1)' : t.lines};
  color: ${p => p.$active ? '#22c55e' : t.muted};
  border: 1px solid ${p => p.$active ? 'rgba(34,197,94,0.2)' : t.lines};
`;

const BadgeDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
`;

const Section = styled.div`margin-bottom: 20px;`;

const SectionLabel = styled.div`
  font-family: ${t.sans}; font-size: 10px; font-weight: 500;
  letter-spacing: 0.16em; text-transform: uppercase;
  color: ${t.muted}; margin-bottom: 8px;
`;

const Card = styled.div`
  background: ${t.white}; border: 1px solid ${t.lines};
  border-radius: 8px; overflow: hidden;
`;

const Row = styled.button`
  display: flex; align-items: center; padding: 13px 16px;
  background: none; border: none; border-bottom: 1px solid ${t.lines};
  cursor: ${p => p.$static ? 'default' : 'pointer'};
  width: 100%; text-align: left; font-family: ${t.sans};
  transition: background 0.12s;
  &:last-child { border-bottom: none; }
  &:hover { background: ${p => p.$static ? 'transparent' : t.bg}; }
`;

const IconBox = styled.div`
  width: 28px; height: 28px; border-radius: 6px;
  background: ${p => p.$bg || t.bg};
  display: flex; align-items: center; justify-content: center;
  margin-right: 12px; flex-shrink: 0; color: ${p => p.$color || t.muted};
`;

const RowLabel = styled.div`
  flex: 1; font-family: ${t.sans}; font-size: 13px; font-weight: 500;
  color: ${p => p.$destructive ? '#ef4444' : t.text};
`;

const RowValue = styled.div`
  font-family: ${t.sans}; font-size: 12px; color: ${t.muted}; margin-right: 6px;
`;

const Chevron = styled.div`color: ${t.muted}; opacity: 0.35;`;

const Overlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(12,12,11,0.75);
  backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  z-index: 2000;
`;

const Modal = styled.div`
  background: ${t.white}; border: 1px solid ${t.lines};
  border-radius: 8px; max-width: 380px; width: 90%;
  padding: 24px 20px 20px;
`;

const ModalTitle = styled.h2`
  font-family: ${t.serif}; margin: 0 0 6px;
  font-size: 20px; font-weight: 900; color: ${t.text};
`;

const ModalText = styled.p`
  font-family: ${t.sans}; margin: 0 0 20px;
  font-size: 13px; color: ${t.muted};
`;

const ModalButtons = styled.div`display: flex; gap: 8px;`;

const ModalBtn = styled.button`
  flex: 1; border: 1px solid ${t.lines}; border-radius: 6px;
  padding: 9px; font-family: ${t.sans}; font-weight: 600;
  font-size: 12px; cursor: pointer;
  background: ${t.white}; color: ${t.text};
  &:hover { background: ${t.bg}; }
`;

const PickerOption = styled.button`
  display: flex; align-items: center; justify-content: space-between;
  width: 100%; padding: 12px 0; background: none; border: none;
  border-bottom: 1px solid ${t.lines}; cursor: pointer;
  font-family: ${t.sans}; font-size: 13px;
  color: ${p => p.$selected ? t.accent : t.text};
  font-weight: ${p => p.$selected ? 600 : 400};
  &:last-child { border-bottom: none; }
  &:hover { color: ${t.text}; }
`;

const CheckMark = styled.div`
  width: 16px; height: 16px; border-radius: 50%;
  border: 1px solid ${p => p.$selected ? t.accent : t.lines};
  background: ${p => p.$selected ? t.accent : 'transparent'};
  display: flex; align-items: center; justify-content: center;
  font-size: 10px; color: #fff; flex-shrink: 0;
`;

const PinDots = styled.div`display: flex; gap: 14px;`;

const PinDot = styled.div`
  width: 14px; height: 14px; border-radius: 50%;
  background: ${p => p.$filled ? t.text : 'transparent'};
  border: 2px solid ${p => p.$filled ? t.text : t.lines};
  transition: all 0.15s;
`;

const Numpad = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 72px);
  grid-template-rows: repeat(4, 72px);
  gap: 10px;
`;

const NumKey = styled.button`
  background: ${p => p.$ghost ? 'transparent' : t.bg};
  border: ${p => p.$ghost ? 'none' : `1px solid ${t.lines}`};
  border-radius: 50%;
  color: ${p => p.$ghost ? t.muted : t.text};
  font-size: ${p => p.$del ? '20px' : '22px'};
  font-weight: 600; cursor: pointer; font-family: inherit;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 2px;
  transition: background 0.1s, transform 0.1s;
  &:active { background: ${t.lines}; transform: scale(0.92); }
`;

const KeySub = styled.div`
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.35;
`;

// ─── Constants ────────────────────────────────────────────────────────────────

const KEYS = [
  { d: '1', s: '' }, { d: '2', s: 'ABC' }, { d: '3', s: 'DEF' },
  { d: '4', s: 'GHI' }, { d: '5', s: 'JKL' }, { d: '6', s: 'MNO' },
  { d: '7', s: 'PQRS' }, { d: '8', s: 'TUV' }, { d: '9', s: 'WXYZ' },
  { d: null }, { d: '0', s: '' }, { d: 'del' },
];

const TIMEOUT_OPTIONS = [
  { label: '1 minuut',   ms: 1 * 60 * 1000 },
  { label: '5 minuten',  ms: 5 * 60 * 1000 },
  { label: '15 minuten', ms: 15 * 60 * 1000 },
  { label: '30 minuten', ms: 30 * 60 * 1000 },
  { label: '1 uur',      ms: 60 * 60 * 1000 },
  { label: '1,5 uur',    ms: 90 * 60 * 1000 },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function KoffiePinSettings() {
  const { hasPin, setPin, removePin, verifyPin, inactivityMs, setInactivity } = useKoffiePinLock();

  const [step, setStep] = useState(null); // null | 'enter' | 'confirm'
  const [pin1, setPin1] = useState('');
  const [pin2, setPin2] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [showRemovePin, setShowRemovePin] = useState(false);
  const [removePin2, setRemovePin2] = useState('');
  const [showVerifyPin, setShowVerifyPin] = useState(false);
  const [verifyPin2, setVerifyPin2] = useState('');
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const currentPin = step === 'enter' ? pin1 : pin2;
  const setCurrentPin = step === 'enter' ? setPin1 : setPin2;

  const press = (digit) => {
    if (currentPin.length >= 6) return;
    const next = currentPin + digit;
    setCurrentPin(next);
    if (next.length === 6) {
      if (step === 'enter') {
        setTimeout(() => setStep('confirm'), 150);
      } else {
        setTimeout(() => confirmPin(pin1, next), 150);
      }
    }
  };

  const del = () => setCurrentPin(p => p.slice(0, -1));

  useEffect(() => {
    if (!step) return;
    const handle = (e) => {
      if (e.key >= '0' && e.key <= '9') press(e.key);
      if (e.key === 'Backspace') del();
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [step, currentPin]);

  useEffect(() => {
    if (!showRemovePin) return;
    const handle = (e) => {
      if (e.key >= '0' && e.key <= '9') pressRemove(e.key);
      if (e.key === 'Backspace') setRemovePin2(p => p.slice(0, -1));
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [showRemovePin, removePin2]);

  useEffect(() => {
    if (!showVerifyPin) return;
    const handle = (e) => {
      if (e.key >= '0' && e.key <= '9') pressVerify(e.key);
      if (e.key === 'Backspace') setVerifyPin2(p => p.slice(0, -1));
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [showVerifyPin, verifyPin2]);

  const confirmPin = async (p1, p2) => {
    if (p1 !== p2) {
      showToast('Pincodes komen niet overeen', 'error');
      setPin1(''); setPin2(''); setStep('enter');
      return;
    }
    const ok = await setPin(p1);
    showToast(ok ? 'Pincode ingesteld' : 'Er ging iets mis', ok ? 'success' : 'error');
    setStep(null); setPin1(''); setPin2('');
  };

  const handleRemove = async () => {
    const ok = await removePin();
    showToast(ok ? 'Pincode verwijderd' : 'Er ging iets mis', ok ? 'success' : 'error');
  };

  const pressRemove = (digit) => {
    if (removePin2.length >= 6) return;
    const next = removePin2 + digit;
    setRemovePin2(next);
    if (next.length === 6) {
      setTimeout(async () => {
        const verified = await verifyPin(next);
        if (verified) {
          setShowRemovePin(false);
          setRemovePin2('');
          handleRemove();
        } else {
          showToast('Onjuiste pincode', 'error');
          setRemovePin2('');
        }
      }, 150);
    }
  };

  const startSetup = () => { setStep('enter'); setPin1(''); setPin2(''); setToast(null); };
  const startChange = () => { setShowVerifyPin(true); setVerifyPin2(''); setToast(null); };
  const cancel = () => { setStep(null); setPin1(''); setPin2(''); };

  const pressVerify = (digit) => {
    if (verifyPin2.length >= 6) return;
    const next = verifyPin2 + digit;
    setVerifyPin2(next);
    if (next.length === 6) {
      setTimeout(async () => {
        const ok = await verifyPin(next);
        if (ok) {
          setShowVerifyPin(false);
          setVerifyPin2('');
          startSetup();
        } else {
          showToast('Onjuiste pincode', 'error');
          setVerifyPin2('');
        }
      }, 150);
    }
  };

  const currentTimeout = TIMEOUT_OPTIONS.find(o => o.ms === inactivityMs) || TIMEOUT_OPTIONS[1];

  const renderNumpad = (value, onPress, onDel) => (
    <Numpad style={{ margin: '0 auto', width: 'fit-content' }}>
      {KEYS.map(({ d, s }, i) =>
        d === null ? <div key={i} /> :
        d === 'del' ? (
          <NumKey key={i} $ghost $del onClick={onDel}>⌫</NumKey>
        ) : (
          <NumKey key={i} onClick={() => onPress(d)}>
            {d}
            {s && <KeySub>{s}</KeySub>}
          </NumKey>
        )
      )}
    </Numpad>
  );

  return (
    <Container>
        {toast && <Toast $type={toast.type}>{toast.msg}</Toast>}
        <Content>
          <KoffieBackButton to="/Koffiereparatie/settings" />
          <Hero>
            <PageTitle>Pincode</PageTitle>
            <HeroBadge $active={hasPin}>
              <BadgeDot />
              {hasPin ? 'Actief' : 'Niet ingesteld'}
            </HeroBadge>
          </Hero>

          {!step && (
            <Section>
              <SectionLabel>Beveiliging</SectionLabel>
              <Card>
                {hasPin ? (
                  <>
                    <Row onClick={startChange}>
                      <IconBox $color="#3b82f6" $bg="rgba(59,130,246,0.08)"><Shield size={14} /></IconBox>
                      <RowLabel>Pincode wijzigen</RowLabel>
                      <Chevron><ChevronRight size={14} /></Chevron>
                    </Row>
                    <Row onClick={() => setShowPicker(true)}>
                      <IconBox $color="#8b5cf6" $bg="rgba(139,92,246,0.08)"><Clock size={14} /></IconBox>
                      <div style={{ flex: 1 }}>
                        <RowLabel>Automatisch vergrendelen</RowLabel>
                      </div>
                      <RowValue>{currentTimeout.label}</RowValue>
                      <Chevron><ChevronRight size={14} /></Chevron>
                    </Row>
                    <Row onClick={() => { setShowRemovePin(true); setRemovePin2(''); }}>
                      <IconBox $color="#ef4444" $bg="rgba(239,68,68,0.08)"><Trash2 size={14} /></IconBox>
                      <RowLabel $destructive>Pincode verwijderen</RowLabel>
                    </Row>
                  </>
                ) : (
                  <Row onClick={startSetup}>
                    <IconBox $color="#3b82f6" $bg="rgba(59,130,246,0.08)"><Shield size={14} /></IconBox>
                    <RowLabel>Pincode instellen</RowLabel>
                    <Chevron><ChevronRight size={14} /></Chevron>
                  </Row>
                )}
              </Card>
            </Section>
          )}
        </Content>

        {step && (
          <Overlay onClick={cancel}>
            <Modal onClick={e => e.stopPropagation()}>
              <ModalTitle style={{ textAlign: 'center' }}>{step === 'enter' ? 'Nieuwe pincode' : 'Bevestig pincode'}</ModalTitle>
              <ModalText style={{ textAlign: 'center' }}>{step === 'enter' ? 'Kies een 6-cijferige pincode.' : 'Voer je pincode nog een keer in.'}</ModalText>
              <PinDots style={{ justifyContent: 'center', marginBottom: 20 }}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <PinDot key={i} $filled={currentPin.length > i} />
                ))}
              </PinDots>
              {renderNumpad(currentPin, press, del)}
              <ModalButtons style={{ marginTop: 20, justifyContent: 'center' }}>
                <ModalBtn onClick={cancel}>Annuleren</ModalBtn>
              </ModalButtons>
            </Modal>
          </Overlay>
        )}

        {showVerifyPin && (
          <Overlay onClick={() => { setShowVerifyPin(false); setVerifyPin2(''); }}>
            <Modal onClick={e => e.stopPropagation()}>
              <ModalTitle style={{ textAlign: 'center' }}>Huidige pincode</ModalTitle>
              <ModalText style={{ textAlign: 'center' }}>Voer je huidige pincode in om door te gaan.</ModalText>
              <PinDots style={{ justifyContent: 'center', marginBottom: 20 }}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <PinDot key={i} $filled={verifyPin2.length > i} />
                ))}
              </PinDots>
              {renderNumpad(verifyPin2, pressVerify, () => setVerifyPin2(p => p.slice(0, -1)))}
              <ModalButtons style={{ marginTop: 20, justifyContent: 'center' }}>
                <ModalBtn onClick={() => { setShowVerifyPin(false); setVerifyPin2(''); }}>Annuleren</ModalBtn>
              </ModalButtons>
            </Modal>
          </Overlay>
        )}

        {showRemovePin && (
          <Overlay onClick={() => { setShowRemovePin(false); setRemovePin2(''); }}>
            <Modal onClick={e => e.stopPropagation()}>
              <ModalTitle style={{ textAlign: 'center' }}>Pincode verwijderen</ModalTitle>
              <ModalText style={{ textAlign: 'center' }}>Voer je huidige pincode in om te bevestigen.</ModalText>
              <PinDots style={{ justifyContent: 'center', marginBottom: 20 }}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <PinDot key={i} $filled={removePin2.length > i} />
                ))}
              </PinDots>
              {renderNumpad(removePin2, pressRemove, () => setRemovePin2(p => p.slice(0, -1)))}
              <ModalButtons style={{ marginTop: 20, justifyContent: 'center' }}>
                <ModalBtn onClick={() => { setShowRemovePin(false); setRemovePin2(''); }}>Annuleren</ModalBtn>
              </ModalButtons>
            </Modal>
          </Overlay>
        )}

        {showPicker && (
          <Overlay onClick={() => setShowPicker(false)}>
            <Modal onClick={e => e.stopPropagation()}>
              <ModalTitle style={{ textAlign: 'center', marginBottom: 4 }}>Automatisch vergrendelen</ModalTitle>
              <ModalText style={{ textAlign: 'center' }}>App vergrendelt na inactiviteit van:</ModalText>
              {TIMEOUT_OPTIONS.map(opt => (
                <PickerOption
                  key={opt.ms}
                  $selected={inactivityMs === opt.ms}
                  onClick={() => { setInactivity(opt.ms); setShowPicker(false); }}
                >
                  {opt.label}
                  <CheckMark $selected={inactivityMs === opt.ms}>
                    {inactivityMs === opt.ms && '✓'}
                  </CheckMark>
                </PickerOption>
              ))}
            </Modal>
          </Overlay>
        )}
      </Container>
  );
}
